import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, PageHeader } from '../components/ui.js';
import { GuideView } from './GuideView.js';
import { downloadGuideDocx } from './download.js';
import type { Guide } from './types.js';

/**
 * A guide read on screen, with the Word copy of the same content a click away. Every
 * document in the help area is rendered through this so the page and the file can
 * never say different things.
 */
export function GuideDocument({
  guide,
  fileName,
  backTo = '/help',
  backLabel = 'All guides',
  showPrint = false,
}: {
  guide: Guide;
  fileName: string;
  backTo?: string;
  backLabel?: string;
  showPrint?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      await downloadGuideDocx(guide, fileName);
    } catch {
      setError('The document could not be generated. Reload the page and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={guide.title}
        subtitle={guide.subtitle}
        actions={
          <div className="flex flex-wrap items-center gap-2 print:hidden">
            <Link to={backTo}>
              <Button variant="secondary">{backLabel}</Button>
            </Link>
            {showPrint && (
              <Button variant="secondary" onClick={() => window.print()}>
                Print or save as PDF
              </Button>
            )}
            <Button onClick={() => void download()} disabled={busy}>
              {busy ? 'Preparing…' : 'Download Word document'}
            </Button>
          </div>
        }
      />
      <p className="mb-5 text-sm text-slate-500">{guide.audience}</p>
      {error && (
        <Alert variant="error" title="Download failed" className="mb-4">
          {error}
        </Alert>
      )}
      <GuideView guide={guide} />
    </div>
  );
}
